import { toast } from '@heidi/design-system/toast';
import { useQueryUser, useUpdateUser } from '@heidi/workspace/data';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useShowSessionMergeInstructions } from './useShowSessionMergeInstructions';

/**
 * Enables auto session merge optimistically (persisted to the server), then
 * opens the instructional modal via `useShowSessionMergeInstructions` — the
 * "Session merge enabled" toast follows its dismissal. Shared by every
 * onboarding surface's "Turn on" CTA. Mirrors iOS
 * `HHSessionMergeOnboardingViewModel`: optimistic via `useUpdateUser`
 * (which reverts on error), no-op while a save is in flight.
 */
export function useEnableSessionMerge(): {
  enableSessionMerge: () => void;
  isEnabling: boolean;
} {
  const intl = useIntl();
  const { showSessionMergeInstructions } = useShowSessionMergeInstructions();
  const { data: sessionSettings, isSuccess } = useQueryUser(
    (user) => user.session_settings
  );
  // enableInvalidation: false — backend session-merge fields not yet deployed,
  // matching useSessionLinkingForm. Remove with the type extension once shipped.
  const { mutate, isPending } = useUpdateUser({ enableInvalidation: false });

  const enableSessionMerge = useCallback(() => {
    // Wait for the user query to succeed before mutating — until then we don't
    // know whether auto-merge is already on, and must not risk a redundant
    // enable. No-op while a save is in flight or the setting is already on.
    if (!isSuccess || isPending || sessionSettings?.enable_auto_session_merge) return;

    mutate(
      { session_settings: { ...sessionSettings, enable_auto_session_merge: true } },
      {
        onSuccess: showSessionMergeInstructions,
        onError: () => {
          toast({
            tone: 'destructive',
            title: intl.formatMessage({
              defaultMessage: 'Could not enable session merge. Please try again.',
              id: 'EnYbmcaB/l',
            }),
          });
        },
      }
    );
  }, [intl, isSuccess, isPending, mutate, showSessionMergeInstructions, sessionSettings]);

  return { enableSessionMerge, isEnabling: isPending };
}
